import React, { useEffect, useMemo, useState } from "react";
import {
  deleteAttendanceRecord,
  deleteAttendanceRecords,
  getAttendance,
  getClasses,
  getStudents,
  markAttendance,
  sendWhatsappTest
} from "../services/api";
import DateInput from "../components/DateInput";
import { confirmAppAction, showAppToast } from "../utils/feedback";
import { formatDate } from "../utils/helpers";
import "./AttendancePage.css";

const getTodayDate = () => new Date().toISOString().split("T")[0];

const normalizeCourse = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return "";
  }

  return trimmed === "Fees" ? "General" : trimmed;
};

function AttendancePage() {
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [selectedClass, setSelectedClass] = useState("all");
  const [selectedBoard, setSelectedBoard] = useState("all");
  const [selectedCourse, setSelectedCourse] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [viewMode, setViewMode] = useState("card");
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [selectedRecordIds, setSelectedRecordIds] = useState([]);
  const [absentIds, setAbsentIds] = useState([]);
  const [sendingWhatsappTest, setSendingWhatsappTest] = useState(false);
  const [loading, setLoading] = useState(false);
  const [studentsLoading, setStudentsLoading] = useState(false);

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    fetchStudents();
  }, [selectedClass]);

  useEffect(() => {
    fetchAttendance();
  }, [selectedDate, selectedClass, selectedBoard, selectedCourse]);

  useEffect(() => {
    setSelectedRecordIds([]);
  }, [selectedStatus]);

  const fetchClasses = async () => {
    try {
      const res = await getClasses();
      setClasses(res.data);
    } catch (err) {
      console.error("Error fetching classes:", err);
    }
  };

  const fetchStudents = async () => {
    try {
      setStudentsLoading(true);
      const res = await getStudents(selectedClass, { includeImages: true });
      setStudents(res.data);
    } catch (err) {
      console.error("Error fetching students:", err);
    } finally {
      setStudentsLoading(false);
    }
  };

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const res = await getAttendance(selectedDate, selectedClass, {
        board: selectedBoard,
        course: selectedCourse
      });
      setAttendanceRecords(res.data);
      setSelectedRecordIds([]);
      setAbsentIds(
        res.data
          .filter((record) => record.attendance_status === "absent")
          .map((record) => String(record.student_id))
      );
    } catch (err) {
      console.error("Error fetching attendance:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckbox = (studentId) => {
    if (absentIds.includes(studentId)) {
      setAbsentIds(absentIds.filter((id) => id !== studentId));
      return;
    }
    setAbsentIds([...absentIds, studentId]);
  };

  const handleMarkAllPresent = () => {
    setAbsentIds([]);
  };

  const handleMarkAllAbsent = () => {
    setAbsentIds(filteredStudents.map((student) => String(student._id)));
  };

  const handleSaveAttendance = async () => {
    if (filteredStudents.length === 0) {
      showAppToast({
        type: "error",
        title: "No students found",
        message: "There are no students available for the selected filters."
      });
      return;
    }

    try {
      const res = await markAttendance({
        absentStudentIds: absentIds,
        classFilter: selectedClass,
        boardFilter: selectedBoard,
        courseFilter: selectedCourse,
        date: selectedDate
      });

      const whatsappSummary = res.data?.whatsapp;
      let message = `Attendance saved for ${formatDate(selectedDate)}.`;

      if (whatsappSummary) {
        message += ` WhatsApp alerts sent: ${whatsappSummary.sent}, skipped: ${whatsappSummary.skipped}, failed: ${whatsappSummary.failed}.`;
      }

      showAppToast({
        type: "success",
        title: "Attendance saved",
        message
      });
      await fetchAttendance();
    } catch (err) {
      console.error("Error marking attendance:", err);
      showAppToast({
        type: "error",
        title: "Failed to save attendance",
        message: err.response?.data?.error || err.message || "Attendance could not be saved."
      });
    }
  };

  const handleSendWhatsappTest = async () => {
    try {
      setSendingWhatsappTest(true);
      const res = await sendWhatsappTest({ date: selectedDate });
      const destination = res.data?.result?.destination;

      showAppToast({
        type: "success",
        title: "Test WhatsApp sent",
        message: destination
          ? `A test message was sent to ${destination}.`
          : "A test WhatsApp message was sent successfully."
      });
    } catch (err) {
      console.error("Error sending test WhatsApp:", err);
      showAppToast({
        type: "error",
        title: "Failed to send test WhatsApp",
        message: err.response?.data?.result?.reason || err.response?.data?.error || err.message || "The test message could not be sent."
      });
    } finally {
      setSendingWhatsappTest(false);
    }
  };

  const handleToggleRecord = (id) => {
    if (selectedRecordIds.includes(id)) {
      setSelectedRecordIds(selectedRecordIds.filter((recordId) => recordId !== id));
      return;
    }
    setSelectedRecordIds([...selectedRecordIds, id]);
  };

  const handleToggleAll = () => {
    if (selectedRecordIds.length === filteredAttendanceRecords.length) {
      setSelectedRecordIds([]);
      return;
    }
    setSelectedRecordIds(filteredAttendanceRecords.map((record) => record._id));
  };

  const handleRemoveAttendance = async (record) => {
    const confirmed = await confirmAppAction({
      title: "Remove attendance record",
      message: `This will remove the attendance record for ${record.student_name}.`,
      confirmLabel: "Remove"
    });

    if (!confirmed) {
      return;
    }

    try {
      await deleteAttendanceRecord(record._id);
      await fetchAttendance();
      showAppToast({
        type: "success",
        title: "Attendance removed",
        message: `The record for ${record.student_name} was removed.`
      });
    } catch (err) {
      console.error("Error deleting attendance:", err);
      showAppToast({
        type: "error",
        title: "Failed to remove attendance",
        message: err.response?.data?.error || err.message || "Attendance record could not be removed."
      });
    }
  };

  const handleBulkRemove = async () => {
    if (selectedRecordIds.length === 0) {
      showAppToast({
        type: "error",
        title: "No records selected",
        message: "Please select attendance records to remove."
      });
      return;
    }

    const confirmed = await confirmAppAction({
      title: "Remove selected attendance",
      message: `This will remove ${selectedRecordIds.length} attendance records.`,
      confirmLabel: "Remove"
    });

    if (!confirmed) {
      return;
    }

    try {
      await deleteAttendanceRecords(selectedRecordIds);
      await fetchAttendance();
      showAppToast({
        type: "success",
        title: "Attendance removed",
        message: `${selectedRecordIds.length} attendance records were removed.`
      });
    } catch (err) {
      console.error("Error bulk deleting attendance:", err);
      showAppToast({
        type: "error",
        title: "Failed to remove selected attendance",
        message: err.response?.data?.error || err.message || "Selected records could not be removed."
      });
    }
  };

  const filteredStudents = useMemo(() => students.filter((student) => {
    const matchesBoard = selectedBoard === "all" || (student.board || "") === selectedBoard;
    const matchesCourse = selectedCourse === "all" || normalizeCourse(student.course) === selectedCourse;
    return matchesBoard && matchesCourse;
  }), [students, selectedBoard, selectedCourse]);

  const filteredAttendanceRecords = useMemo(() => attendanceRecords.filter((record) => (
    selectedStatus === "all" || record.attendance_status === selectedStatus
  )), [attendanceRecords, selectedStatus]);

  const boardOptions = useMemo(() => Array.from(
    new Set(students.map((student) => (student.board || "").trim()).filter(Boolean))
  ).sort(), [students]);

  const courseOptions = ["Science", "Commerce", "General"];
  const presentCount = filteredAttendanceRecords.filter((record) => record.attendance_status === "present").length;
  const absentCount = filteredAttendanceRecords.filter((record) => record.attendance_status === "absent").length;

  return (
    <div className="attendance-page">
    <div className="attendance-page__header">
      {/* <h1>Attendance Module</h1> */}
     {/*<p>Mark attendance by date here, then review or bulk-remove saved records below.</p> */}
      </div>

      <div className="attendance-page__filters">
        <div className="attendance-page__toolbar-group attendance-page__toolbar-group--left">
          <div className="attendance-page__filter-card attendance-page__filter-card--compact">
            <label htmlFor="attendance-date">Date</label>
            <DateInput id="attendance-date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
          </div>

          <div className="attendance-page__filter-card attendance-page__filter-card--compact">
            <label htmlFor="attendance-class">Class</label>
            <select id="attendance-class" value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
              <option value="all">All Classes</option>
              {classes.map((cls) => (
                <option key={cls} value={cls}>{cls}</option>
              ))}
            </select>
          </div>

          <div className="attendance-page__filter-card attendance-page__filter-card--compact">
            <label htmlFor="attendance-status">Status</label>
            <select id="attendance-status" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
              <option value="all">All Statuses</option>
              <option value="present">Present</option>
              <option value="absent">Absent</option>
            </select>
          </div>

          <div className="attendance-page__filter-card attendance-page__filter-card--compact">
            <label htmlFor="attendance-board">Board</label>
            <select id="attendance-board" value={selectedBoard} onChange={(e) => setSelectedBoard(e.target.value)}>
              <option value="all">All Boards</option>
              {boardOptions.map((board) => (
                <option key={board} value={board}>{board}</option>
              ))}
            </select>
          </div>

          <div className="attendance-page__filter-card attendance-page__filter-card--compact">
            <label htmlFor="attendance-course">Course</label>
            <select id="attendance-course" value={selectedCourse} onChange={(e) => setSelectedCourse(e.target.value)}>
              <option value="all">All Courses</option>
              {courseOptions.map((course) => (
                <option key={course} value={course}>{course}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="attendance-page__toolbar-group attendance-page__toolbar-group--right">
          <div className="attendance-page__view-toggle">
            <span className="attendance-page__view-label">View</span>
            <button
              className={`attendance-page__view-btn ${viewMode === "card" ? "active" : ""}`}
              onClick={() => setViewMode("card")}
            >
              Card
            </button>
            <button
              className={`attendance-page__view-btn ${viewMode === "detail" ? "active" : ""}`}
              onClick={() => setViewMode("detail")}
            >
              Detail
            </button>
          </div>
        </div>
      </div>

      <div className="attendance-page__stats">
        <div className="attendance-page__stat-card">
          <h3>Present</h3>
          <p>{presentCount}</p>
        </div>
        <div className="attendance-page__stat-card">
          <h3>Absent</h3>
          <p>{absentCount}</p>
        </div>
        <div className="attendance-page__stat-card">
          <h3>Total Records</h3>
          <p>{filteredAttendanceRecords.length}</p>
        </div>
      </div>

      <div className="attendance-page__table-card">
        <div className="attendance-page__table-header">
          <h2>Mark Attendance</h2>
          <span>{formatDate(selectedDate)}</span>
        </div>

        {studentsLoading && <p className="attendance-page__empty">Loading students...</p>}

        {!studentsLoading && filteredStudents.length === 0 && (
          <p className="attendance-page__empty">No students found for the selected filters.</p>
        )}

        {!studentsLoading && filteredStudents.length > 0 && (
          <>
            {viewMode === "card" && (
              <div className="attendance-page__mark-grid">
                {filteredStudents.map((student) => (
                  <div className="attendance-page__student-card" key={student._id}>
                    <div className="attendance-page__student-main">
                      {student.imageData || student.imageUrl ? (
                        <img src={student.imageData || student.imageUrl} alt={student.name} className="attendance-page__avatar" />
                      ) : (
                        <div className="attendance-page__avatar attendance-page__avatar--placeholder">
                          {student.name?.slice(0, 1)?.toUpperCase() || "S"}
                        </div>
                      )}
                      <div>
                        <h3>{student.name}</h3>
                        <p>Class: {student.class}</p>
                        <p>Roll Number: {student.rollNumber}</p>
                      </div>
                    </div>
                    <label className="attendance-page__checkbox-row">
                      <input
                        type="checkbox"
                        checked={absentIds.includes(student._id)}
                        onChange={() => handleCheckbox(student._id)}
                      />
                      Mark absent
                    </label>
                  </div>
                ))}
              </div>
            )}

            {viewMode === "detail" && (
              <div className="attendance-page__table-wrap">
                <table className="attendance-page__table attendance-page__table--marking">
                  <thead>
                    <tr>
                      <th>Photo</th>
                      <th>Name</th>
                      <th>Class</th>
                      <th>Roll Number</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map((student) => (
                      <tr key={student._id}>
                        <td>
                          {student.imageData || student.imageUrl ? (
                            <img
                              src={student.imageData || student.imageUrl}
                              alt={student.name}
                              className="attendance-page__table-avatar"
                            />
                          ) : (
                            <div className="attendance-page__table-avatar attendance-page__table-avatar--placeholder">
                              {student.name?.slice(0, 1)?.toUpperCase() || "S"}
                            </div>
                          )}
                        </td>
                        <td>{student.name}</td>
                        <td>{student.class}</td>
                        <td>{student.rollNumber}</td>
                        <td>
                          <label className="attendance-page__checkbox-row">
                            <input
                              type="checkbox"
                              checked={absentIds.includes(student._id)}
                              onChange={() => handleCheckbox(student._id)}
                            />
                            Mark absent
                          </label>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="attendance-page__actions">
              <button className="attendance-page__secondary-btn" onClick={handleMarkAllPresent}>
                Mark All Present
              </button>
              <button className="attendance-page__secondary-btn" onClick={handleMarkAllAbsent}>
                Mark All Absent
              </button>
              <button
                className="attendance-page__secondary-btn"
                onClick={handleSendWhatsappTest}
                disabled={sendingWhatsappTest}
              >
                {sendingWhatsappTest ? "Sending Test..." : "Send Test WhatsApp"}
              </button>
              <button className="attendance-page__primary-btn" onClick={handleSaveAttendance}>
                Save Attendance and Send Absence Alerts
              </button>
            </div>
          </>
        )}
      </div>

      <div className="attendance-page__table-card">
        <div className="attendance-page__table-header">
          <h2>Attendance Records</h2>
          <span>{formatDate(selectedDate)}</span>
        </div>

        {!loading && filteredAttendanceRecords.length > 0 && (
          <div className="attendance-page__actions">
            <button className="attendance-page__remove-btn" onClick={handleBulkRemove}>
              Remove Selected ({selectedRecordIds.length})
            </button>
          </div>
        )}

        {loading && <p className="attendance-page__empty">Loading attendance...</p>}

        {!loading && filteredAttendanceRecords.length === 0 && (
          <p className="attendance-page__empty">No attendance records found for the selected date.</p>
        )}

        {!loading && filteredAttendanceRecords.length > 0 && (
          <div className="attendance-page__table-wrap">
            <table className="attendance-page__table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={filteredAttendanceRecords.length > 0 && selectedRecordIds.length === filteredAttendanceRecords.length}
                      onChange={handleToggleAll}
                    />
                  </th>
                  <th>Student Name</th>
                  <th>Class</th>
                  <th>Roll Number</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredAttendanceRecords.map((record) => (
                  <tr key={record._id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedRecordIds.includes(record._id)}
                        onChange={() => handleToggleRecord(record._id)}
                      />
                    </td>
                    <td>{record.student_name}</td>
                    <td>{record.class}</td>
                    <td>{record.rollNumber}</td>
                    <td>
                      <span className={`attendance-page__badge attendance-page__badge--${record.attendance_status}`}>
                        {record.attendance_status}
                      </span>
                    </td>
                    <td>{formatDate(record.date)}</td>
                    <td>
                      <button className="attendance-page__remove-btn" onClick={() => handleRemoveAttendance(record)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AttendancePage;
